import * as React from 'react';
import styled, { css } from 'styled-components';
import { rem } from 'polished';
import { __, __r } from 'react-i18n/lib';
import { Link } from 'react-router';
import { Button } from '../../Zemplin/General/Button';
import { ErrorAlert } from '../../Defaults/Alerts/ErrorAlert';
import { DsiInput as Input } from '../General/Input';
import { InfoText, InfoType } from '../General/InfoText';
import { DsiInfoText, DsiInfoType } from '../../..';

interface Props {
  cartId: string | undefined;
  pathname: string;
  search: string;
  baseApiUrl: string | undefined;
  baseUrl: string | undefined;
  error: string;
  lang: string;
  domainId: number;
  showRegisterSuccess?: boolean;
  loginType?: string;
}

function LoginForm({
  cartId,
  pathname,
  search,
  baseApiUrl,
  baseUrl,
  error,
  lang,
  domainId,
  showRegisterSuccess,
  loginType,
}: Props) {
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');

  const formRef = React.useRef(null);
  React.useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === 'Enter' && formRef?.current) {
        (formRef?.current as any)?.requestSubmit();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <Wrapper>
      <Header>{`${__('Prihlásenie B2C')}`}</Header>
      {showRegisterSuccess && (
        <InfoText
          type={InfoType.SUCCESS}
          info={__('Registrácia prebehla úspešne, teraz sa môžete prihlásiť.')}
        />
      )}
      <Form
        action={`${baseApiUrl}/oauth/authorize?client_id=eshop&scope=all&lang=${lang}&cart_id=${cartId}&domain_id=${domainId}&redirect_uri=${baseUrl}${__r(
          'routes:prihlasenie',
          '/prihlasenie',
        )}?cart_id=${cartId}&state=${`${baseUrl}${pathname}${
          search && search !== '' ? `%3F${search}` : ''
        }`}`}
        method="POST"
        autoComplete="true"
        ref={formRef}
      >
        <StyledLabel htmlFor="username">
          {loginType === 'b2c'
            ? `${__('E-mail')}`
            : `${__('Prihlasovacie meno/E-mail')}`}
        </StyledLabel>
        <FormInput
          type="text"
          value={email}
          name="username"
          // tslint:disable-next-line:jsx-no-lambda
          onChange={e => setEmail(e.target.value)}
          tabIndex={1}
        />
        <StyledLabel htmlFor="password">{`${__('Heslo')}`}</StyledLabel>
        <FormInput
          type="password"
          value={password}
          name="password"
          // tslint:disable-next-line:jsx-no-lambda
          onChange={e => setPassword(e.target.value)}
          tabIndex={2}
        />
        <PasswordLink to={__r('routes:zabudnute-heslo', '/zabudnute-heslo')}>
          <ForgotPassword>{__('Zabudli ste heslo')}?</ForgotPassword>
        </PasswordLink>

        {error ? (
          <DsiInfoText
            marginBottom={0}
            marginTop={12}
            type={DsiInfoType.ERROR}
            info={__(error)}
          />
        ) : (
          <></>
        )}

        <LoginButton type="submit">{__('Prihlásiť sa užívateľ')}</LoginButton>
      </Form>
      <NoAccountWrapper>
        {`${__('Nemáte účet')}? `}
        <Link to={__r('routes:registracia', '/registracia')}>
          <Register>{__('Vytvoriť účet')}</Register>
        </Link>
      </NoAccountWrapper>
    </Wrapper>
  );
}

export const secondaryText = css`
  color: ${({ theme }) => theme.colors.textPrimary};
  text-decoration-color: ${({ theme }) => theme.colors.textPrimary};
  font-family: ${({ theme }) => theme.font.primary};
  font-weight: 400;
  font-size: ${rem(14)};
  line-height: ${rem(22)};
`;

export const Wrapper = styled.div`
  width: 100%;
  max-width: ${rem(400)};
  margin: ${rem(0)} auto;
  background: ${({ theme }) => theme.colors.white};
  padding: ${rem(32)} ${rem(24)};
  background: #ffffff 0% 0% no-repeat padding-box;
  box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.08);
  border-radius: 4px;

  ${({ theme }) => theme.mediab.l925`
     padding: ${rem(24)} ${rem(16)};
     margin: 0 auto;
  `}
`;

const Header = styled.h1`
  font-size: ${rem(30)};
  font-weight: 400;
  position: relative;
  font-family: ${({ theme }) => theme.font.primary};
  margin-bottom: ${rem(32)};
  line-height: ${rem(38)};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const Form = styled.form`
  margin: 0;
  & input:first-of-type {
    margin-bottom: ${rem(16)};
  }
`;

export const LoginButton = styled(Button)`
  height: ${rem(48)};
  margin: ${rem(15)} 0 0;
  padding: ${rem(15)};
  width: 100%;
  max-width: ${rem(400)};
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  font-size: ${rem(14)};
  font-weight: 500;
  border-radius: ${rem(2)};
  margin: ${rem(32)} 0;
  box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.08);
  border-radius: 4px;
`;

const PasswordLink = styled(Link)`
  text-decoration-color: ${({ theme }) => theme.colors.primary};
`;

const ForgotPassword = styled.p`
  ${secondaryText};
  color: ${({ theme }) => theme.colors.primary};
  text-align: right;
  margin: ${rem(16)} 0 0;
  width: 100%;
`;

export const Register = styled.p`
  ${secondaryText};
  color: ${({ theme }) => theme.colors.primary};
  text-decoration-color: ${({ theme }) => theme.colors.primary};
  display: inline-block;
  text-decoration: underline;
  margin: 0;
  margin-left: ${rem(4)};
`;

export const NoAccountWrapper = styled.span`
  ${secondaryText};
  display: flex;
  justify-content: center;
`;

export const StyledLabel = styled.label<{ marginBottom?: number }>`
  font-size: ${rem(16)};
  font-weight: 400;
  color: ${({ theme }) => theme.colors.textPrimary};
  display: flex;
  flex-direction: row;
  margin-bottom: ${({ marginBottom }) =>
    marginBottom ? rem(marginBottom) : 0};
`;

export const FormInput = styled(Input)<{ marginRight?: number }>`
  width: 100%;
  margin-top: ${rem(8)};
  margin-right: ${({ marginRight }) => (marginRight ? rem(marginRight) : 0)};
  font-size: ${rem(14)};
  ${({ theme }) => theme.mediab.m600`
    max-width: ${rem(600)};
  `}
`;

export { LoginForm as DsiLoginForm };
